/**
 * Native app boot hook.
 *
 * Called once from the app entry to configure native-only chrome:
 *   - style the status bar so content doesn't overlap it,
 *   - hide the splash screen once the React tree has mounted.
 *
 * All side effects guarded by `isNative()` so the web build is unaffected.
 * Plugins are lazy-imported to keep the initial bundle lean.
 */

import { isNative } from './index'

export async function initializeNativeShell(): Promise<void> {
  if (!isNative()) return

  try {
    const statusBarMod = await import(/* @vite-ignore */ '@capacitor/status-bar')
    const styleEnum = statusBarMod.Style ?? { Dark: 'DARK', Light: 'LIGHT', Default: 'DEFAULT' }
    // Light icons on a dark status bar matches the slate-950 app shell.
    await statusBarMod.StatusBar.setStyle({ style: styleEnum.Dark })
    // Do NOT overlay — React can size its top header normally.
    if (typeof statusBarMod.StatusBar.setOverlaysWebView === 'function') {
      await statusBarMod.StatusBar.setOverlaysWebView({ overlay: false })
    }
  } catch {
    /* plugin missing — safe to skip */
  }

  try {
    const splashMod = await import(/* @vite-ignore */ '@capacitor/splash-screen')
    await splashMod.SplashScreen.hide({ fadeOutDuration: 200 })
  } catch {
    /* plugin missing — safe to skip */
  }
}
